import React from 'react';
import ColorSwatch, { ColorSwatchType } from './ColorSwatch';
import SelectableBase, { SelectableBaseProps } from '../SelectableBase';
import Box from '../Box';
import ButtonBase from '../ButtonBase';
import Tooltip from '../Tooltip';
import styles from './colorSwatchButton.module.scss';

export type ColorSwatchProps = ColorSwatchType &
  Pick<
    SelectableBaseProps,
    // Note: SelectableBase allows more props than we actually need.
    // We only use some of the props returned by SingleSelectBase's ItemRenderData.
    'id' | 'isSelected' | 'like' | 'name' | 'onChange' | 'value'
  > & {
    /**
     * Hides the Tooltip
     */
    hideTooltip?: boolean;
    /**
     * Tooltip comes with a required Provider by default, but this can be
     * skipped so that consumers can provide their own @radix-ui/tooltip Provider
     * See: https://www.radix-ui.com/primitives/docs/components/tooltip#provider
     */
    skipProvider?: boolean;
  };

/**
 * A single color swatch  button component, used by the `ColorSwatches` component.
 * Avoid using the single `ColorSwatchButton` component directly in consuming applications,
 * unless otherwise needing a UI differing from the default `ColorSwatches` component.
 */
const ColorSwatchButton = ({
  hideTooltip,
  label,
  hex,
  url,
  isOutOfStock,
  size,
  skipProvider = false,
  ...selectableProps
}: ColorSwatchProps): JSX.Element => {
  const button = (
    <SelectableBase {...selectableProps}>
      {({ input, isSelected, as, ...buttonProps }) => (
        <ButtonBase
          as={as}
          className={styles['color-swatch-button']}
          {...buttonProps}
        >
          <ColorSwatch
            label={label}
            hex={hex}
            url={url}
            isOutOfStock={isOutOfStock}
            isSelected={isSelected}
            size={size}
            type="interactive"
          />
          {input}
        </ButtonBase>
      )}
    </SelectableBase>
  );

  if (hideTooltip) {
    return button;
  }

  return (
    <Tooltip content={label} skipProvider={skipProvider}>
      <Box display="inline-block">{button}</Box>
    </Tooltip>
  );
};

export default ColorSwatchButton;
